import PropTypes from 'prop-types';
import React from 'react';
import {container} from '../../ioc/container';
import {services} from '../../ioc/services';
import {ISiteInfo, SiteInfoContext} from '../../services/env/siteInfo/siteInfoContext';
import {fallbackSiteSettings} from '../../services/env/siteSettings/fallbackSiteSettings';
import {ExperimentsContext} from '../../services/experiments/useExperiments';
import {IAlbumProvider} from '../../services/injectable/AlbumProvider/IAlbumProvider';
import {IExperimentsApi} from '../../services/injectable/ExperimentsApi/IExperimentsApi';
import {ISiteInfoProvider} from '../../services/injectable/SiteInfoProvider/ISiteInfoProvider';
import {ISiteSettingsProvider} from '../../services/injectable/SiteSettingsProvider/ISiteSettingsProvider';
import {mapValues} from '../../services/utils/mapValues';
import {SiteSettingsProvider} from './siteSettings/SiteSettingsProvider';

interface IOuter {
  albumId: string;
  loadingComponent: React.ComponentType;
  errorComponent: React.ComponentType;
  children: ({album}) => React.ReactNode;
}

interface IState {
  isLoading: boolean;
  isError: boolean;
  experiments?: Record<string, string>;
  siteInfo?: ISiteInfo;
  data?: any;
  siteSettings?: any;
}

class DataContainer extends React.Component<IOuter, IState> {
  static propTypes = {
    albumId: PropTypes.string.isRequired,
    loadingComponent: PropTypes.func,
    errorComponent: PropTypes.func,
    children: PropTypes.func.isRequired,
  };
  private isComponentMounted: boolean;

  constructor(props, context) {
    super(props, context);
    this.state = {
      isLoading: true,
      isError: false,
      experiments: undefined,
      siteInfo: undefined,
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;
    const {albumId} = this.props;
    const setState = (name: keyof IState, value?) => (resolved) => {
      if (this.isComponentMounted) {
        this.setState({[name]: value === undefined ? resolved : value} as any);
      }
    };
    const albumProvider = container.get<IAlbumProvider>(services.albumProvider);
    const siteSettingProvider = container.get<ISiteSettingsProvider>(services.siteSettings);
    const siteInfoProvider = container.get<ISiteInfoProvider>(services.siteInfo);
    const experimentsApi = container.get<IExperimentsApi>(services.experiments);

    Promise.all([
      albumProvider.fetch(albumId).then(setState('data')),

      siteSettingProvider.obtain().then(setState('siteSettings')),

      experimentsApi.getExperiments('wix-music').then(setState('experiments')),

      siteInfoProvider.obtain().then(setState('siteInfo')),
    ])
      .catch(setState('isError', true))
      .then(setState('isLoading', false));
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  render() {
    const {loadingComponent: LoadingComponent, errorComponent: ErrorComponent} = this.props;
    const {isLoading, isError, data, siteSettings, experiments, siteInfo} = this.state;
    if (isError && !isLoading) {
      return (
        <SiteSettingsProvider siteSettings={siteSettings || fallbackSiteSettings}>
          <ErrorComponent />
        </SiteSettingsProvider>
      );
    }
    if (isLoading || !data || !siteSettings) {
      return <LoadingComponent />;
    }

    const {album} = data;
    const allExperiments = mapValues(
      {
        ...experiments,
      },
      (value) => String(value),
    );

    return (
      <div className="container">
        <SiteInfoContext.Provider value={siteInfo}>
          <ExperimentsContext.Provider value={allExperiments}>
            <SiteSettingsProvider siteSettings={siteSettings}>{this.props.children({album})}</SiteSettingsProvider>
          </ExperimentsContext.Provider>
        </SiteInfoContext.Provider>
      </div>
    );
  }
}

export {DataContainer};
